import _ from 'lodash';

import { IFunctionForm } from './function-form';
import { roleListData, RoleListData } from '../../role/models/role-list-data';
import { IFunctionDto, functionDto } from 'src/assets/common/models/function-dto.g';
import { ILevelDomainData, levelDomainData } from 'src/app/infrastructure/models/level-domain-data';
import { roleData, RoleData } from '../../role/models/role-data';
import { IFunctionRefDto } from 'src/assets/common/models/function-ref-dto.g';
import { functionCreditTypeDto } from 'src/assets/common/models/function-credit-type-dto.g';
import { AutoComplete, autocomplete, IAutoComplete } from '../../shared/controls/models/auto-complete.model';

export interface IFunctionCache {
    data: IFunctionData;
    url: string;
}

export const functionData = {
    ...levelDomainData,
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    displayName: 'displayName',
    isAdmin: 'isAdmin',
    appFunction: 'appFunction',
    dUFunction: 'dUFunction',
    oMFunction: 'oMFunction',
    default: 'default',
    system: 'system',
    deleted: 'deleted',    
    inactive: 'inactive',

    rolesData: 'rolesData',

    // CreditType (creditplan): nu en, als al vastgelegd, vanaf een volgende maand. creditType = de keuze in het
    // formulier (alleen met claim FunctionEdit/CreditType/Update); creditType_guid gaat mee bij opslaan.
    creditType: 'creditType',
    creditType_guid: 'creditType_guid',
    creditType_name: 'creditType_name',
    creditType_credits: 'creditType_credits',
    creditTypeNext_name: 'creditTypeNext_name',
    creditTypeNext_credits: 'creditTypeNext_credits',
    creditTypeNext_validFrom: 'creditTypeNext_validFrom',
    creditType_hasHistory: 'creditType_hasHistory',
} as const;

export type TFunctionData = (typeof functionData)[keyof typeof functionData];

export interface IFunctionData extends ILevelDomainData {
    [functionData.guid]: string;
    [functionData.nr]: number | null;
    [functionData.name]: string | null;
    [functionData.displayName]: string | null;
    [functionData.isAdmin]: boolean | null;
    [functionData.appFunction]: boolean | null;
    [functionData.dUFunction]: boolean | null;
    [functionData.oMFunction]: boolean | null;
    [functionData.default]: boolean | null;
    [functionData.system]: boolean | null;
    [functionData.deleted]: boolean;
    [functionData.inactive]: boolean;

    [functionData.rolesData]: RoleData[] | null;

    [functionData.creditType]: IAutoComplete | null;
    [functionData.creditType_guid]: string | null;
    [functionData.creditType_name]: string | null;
    [functionData.creditType_credits]: number | null;
    [functionData.creditTypeNext_name]: string | null;
    [functionData.creditTypeNext_credits]: number | null;
    [functionData.creditTypeNext_validFrom]: string | null;
    [functionData.creditType_hasHistory]: boolean;
}

export class FunctionData
    implements IFunctionData, ILevelDomainData
{
    [functionData.guid]!: string;
    [functionData.nr]: number | null = null;
    [functionData.name]: string | null = null;
    [functionData.displayName]: string | null = null;
    [functionData.isAdmin]: boolean | null = null;
    [functionData.appFunction]: boolean | null = null;
    [functionData.dUFunction]: boolean | null = null;
    [functionData.oMFunction]: boolean | null = null;
    [functionData.default]: boolean | null = null;
    [functionData.system]: boolean | null = null;
    [functionData.deleted]!: boolean;
    [functionData.inactive]!: boolean;

    [functionData.rolesData]: RoleData[] | null = null;

    [functionData.creditType]: IAutoComplete | null = null;
    [functionData.creditType_guid]: string | null = null;
    [functionData.creditType_name]: string | null = null;
    [functionData.creditType_credits]: number | null = null;
    [functionData.creditTypeNext_name]: string | null = null;
    [functionData.creditTypeNext_credits]: number | null = null;
    [functionData.creditTypeNext_validFrom]: string | null = null;
    [functionData.creditType_hasHistory] = false;

    protected constructor(modelDto?: IFunctionDto | null);
    protected constructor(modelData: IFunctionData, modelForm: IFunctionForm);
    protected constructor(value?: IFunctionDto | IFunctionData | null, modelForm?: IFunctionForm) {
        if (!!modelForm) {
            const _modelData = value as IFunctionData;
            _.assign(this, _modelData, modelForm);
            this[functionData.creditType_guid] = (modelForm[functionData.creditType]?.[autocomplete.itemId] as string) ?? _modelData[functionData.creditType_guid];
        } else if (!!value) {
            const _modelDto = value as IFunctionDto;

            this[functionData.guid] = _modelDto[functionDto.guid]!;
            this[functionData.nr] = _modelDto[functionDto.nr];
            this[functionData.name] = _modelDto[functionDto.name];
            this[functionData.displayName] = _modelDto[functionDto.displayName];
            this[functionData.isAdmin] = _modelDto[functionDto.isAdmin];
            this[functionData.appFunction] = _modelDto[functionDto.appFunction];
            this[functionData.dUFunction] = _modelDto[functionDto.duFunction];
            this[functionData.oMFunction] = _modelDto[functionDto.omFunction];
            this[functionData.default] = _modelDto[functionDto.default];
            this[functionData.system] = _modelDto[functionDto.system];
            this[functionData.deleted] = _modelDto[functionDto.deleted]!;
            this[functionData.inactive] = _modelDto[functionDto.inactive]!;

            this[functionData.rolesData] = _modelDto?.rolesDto
                ?.map(item => RoleData.create(item))
                .filter((item): item is RoleData => item !== null && item[roleData.deleted] === false)
                .sort((a, b) => (a[roleData.name]?.localeCompare(b?.[roleListData.name] ?? '')) ?? 0)
                ?? [];

            // Het formulier toont het laatst vastgelegde type (dat van een volgende maand als dat er al is).
            const _creditType = _modelDto[functionDto.creditTypeInfo];
            this[functionData.creditType_guid] = _creditType?.[functionCreditTypeDto.nextCreditTypeGuid] ?? _creditType?.[functionCreditTypeDto.creditTypeGuid] ?? null;
            this[functionData.creditType_name] = _creditType?.[functionCreditTypeDto.creditTypeName] ?? null;
            this[functionData.creditType_credits] = _creditType?.[functionCreditTypeDto.credits] ?? null;
            this[functionData.creditTypeNext_name] = _creditType?.[functionCreditTypeDto.nextCreditTypeName] ?? null;
            this[functionData.creditTypeNext_credits] = _creditType?.[functionCreditTypeDto.nextCredits] ?? null;
            this[functionData.creditTypeNext_validFrom] = _creditType?.[functionCreditTypeDto.nextValidFrom] ?? null;
            this[functionData.creditType_hasHistory] = !!_creditType?.[functionCreditTypeDto.hasHistory];
            this[functionData.creditType] = this[functionData.creditType_guid]
                ? AutoComplete.create(this[functionData.creditType_guid]!, this[functionData.creditTypeNext_name] ?? this[functionData.creditType_name] ?? '')
                : null;
        }
    }
        
    static create(modelDto?: IFunctionDto | IFunctionRefDto | null): IFunctionData | null
    static create(modelData: IFunctionData, modelForm: IFunctionForm): IFunctionData | null
    static create(value?: IFunctionDto | IFunctionRefDto | IFunctionData | null, modelForm?: IFunctionForm): IFunctionData | null {

        if (!!modelForm) {
            const _modelData = value as IFunctionData;
            return new FunctionData(_modelData, modelForm);
        } else {
            const _modelDto = value as IFunctionDto;
            return _modelDto ? new FunctionData(_modelDto) : null;
        }
    }
}
